import { Fragment } from "react";
import { TestIdClassNameProps } from "@app/types/Common";
import { Element } from "@app/types/Cue";
import { Name } from "@app/types/enums";
import { Text } from "@blocks/Blocks";
import { getFieldValue, getListTag } from "@util/helpers";
import cx from "classnames";

import DynamicIframeWrapper from "./DynamicIframe/DynamicIframeWrapper";
import ArticleInlineImage from "./ArticleInlineImage";

export type BoxProps = TestIdClassNameProps & {
  box: Element[];
  content: Element[];
};

export default function Box({
  rootClassName,
  box,
  content,
}: BoxProps): React.ReactElement {
  return (
    <div
      className={cx(
        rootClassName,
        "mb-6 mt-4 border border-gray-450 px-4 py-3"
      )}
      data-testid="box-component"
    >
      {box?.map((item) => {
        switch (item.type) {
          case "box_title":
            return (
              <strong key={item.id} className="mb-6">
                {item.fields?.[0]?.value}
              </strong>
            );

          case "paragraph": {
            if (!item.fields?.[0]?.value) break;
            const paragraphText = item.fields?.[0].value;
            return (
              <Text
                key={item.id}
                text={paragraphText}
                annotations={item.fields?.[0].annotations}
                annotationContext={content.filter((blocks) =>
                  item.fields?.[0]?.annotations
                    ?.map((a) => a.value)
                    .includes(blocks.id)
                )}
              />
            );
          }

          case "image":
            return (
              <Fragment key={item.id}>
                {item.relation ? (
                  <ArticleInlineImage
                    fields={item.fields}
                    relation={item.relation}
                    annotation={item.type}
                    rootClassName="mb-6"
                  />
                ) : null}
              </Fragment>
            );

          case "list_bulleted":
          case "list_numbered": {
            const ListTag = getListTag(item.type);

            return (
              <ListTag
                key={item.id}
                data-annotation={item.type}
                className={cx("mb-6 pl-8", {
                  "list-disc": ListTag === "ul",
                  "list-decimal": ListTag === "ol",
                })}
              >
                {item.children
                  ?.flatMap((id) =>
                    content.filter((_element) => _element.id === id)
                  )
                  .map((item) => {
                    return (
                      <li key={item.id}>
                        {getFieldValue(item.fields, Name.Paragraph) as string}
                      </li>
                    );
                  })}
              </ListTag>
            );
          }

          case "dynamic_iframe_wrapper":
            return (
              <DynamicIframeWrapper
                key={item.id}
                fields={item.fields}
                annotation={item.type}
                rootClassName="mb-6"
              />
            );

          default:
            return null;
        }
      })}
    </div>
  );
}
